import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { Check as CheckIcon, X as XIcon, Eye as EyeIcon } from 'react-feather';
import swal from 'sweetalert';

const PerfilAfiliadosListResults = ({ profiles, onApprove, onReject, onViewDetails, ...rest }) => {
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleApproveClick = async (profile) => {
    const result = await swal({
      title: 'Aprovar Perfil',
      text: `Deseja aprovar o perfil de ${profile.user.name}?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Aprovar'],
      dangerMode: false
    });

    if (result) {
      onApprove(profile.id);
    }
  };

  const handleRejectClick = (profile) => {
    setSelectedProfile(profile);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectionReason.trim()) {
      swal('Atenção', 'Informe o motivo da rejeição', 'warning');
      return;
    }

    onReject(selectedProfile.id, rejectionReason);
    setRejectDialogOpen(false);
    setSelectedProfile(null);
    setRejectionReason('');
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setSelectedProfile(null);
    setRejectionReason('');
  };

  return (
    <>
      <Card {...rest}>
        <PerfectScrollbar>
          <Box sx={{ minWidth: 1050 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Afiliado</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>CPF/CNPJ</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data de Cadastro</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.slice(page * limit, page * limit + limit).map((profile) => (
                  <TableRow hover key={profile.id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {profile.user.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{profile.user.email}</TableCell>
                    <TableCell>{profile.cpf_cnpj || '-'}</TableCell>
                    <TableCell>{profile.phone || '-'}</TableCell>
                    <TableCell>
                      {profile.status === 'pending' && (
                        <Chip label="Pendente" color="warning" size="small" />
                      )}
                      {profile.status === 'approved' && (
                        <Chip label="Aprovado" color="success" size="small" />
                      )}
                      {profile.status === 'rejected' && (
                        <Chip label="Rejeitado" color="error" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(profile.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            size="small"
                            onClick={() => onViewDetails(profile)}
                          >
                            <EyeIcon size={18} />
                          </IconButton>
                        </Tooltip>
                        {profile.status === 'pending' && (
                          <>
                            <Tooltip title="Aprovar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleApproveClick(profile)}
                              >
                                <CheckIcon size={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rejeitar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRejectClick(profile)}
                              >
                                <XIcon size={18} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </PerfectScrollbar>
        <TablePagination
          component="div"
          count={profiles.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Registros por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Card>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleRejectCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Rejeitar Perfil de Afiliado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Informe o motivo da rejeição do perfil de{' '}
            <strong>{selectedProfile?.user?.name}</strong>:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Motivo da Rejeição"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleRejectConfirm} color="error" variant="contained">
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

PerfilAfiliadosListResults.propTypes = {
  profiles: PropTypes.array.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired
};

export default PerfilAfiliadosListResults;
